/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

// src/components/sale-page/TrackingScripts.tsx
// Injects Facebook Pixel, Google Analytics 4, Google Tag Manager scripts

import Script from 'next/script'
import type { PageTracking } from '@/../types/sale-page'

interface Props {
  tracking: PageTracking | null
  consentGiven?: boolean  // If cookie consent is enabled, only load when true
}

export default function TrackingScripts({ tracking, consentGiven = true }: Props) {
  if (!tracking) return null
  // If consent is required but not given, don't load tracking scripts
  if (!consentGiven) return null

  return (
    <>
      {/* Facebook Pixel */}
      {tracking.fbPixelId && (
        <>
          <Script id="fb-pixel" strategy="afterInteractive">
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${tracking.fbPixelId}');
              fbq('track', 'PageView');
            `}
          </Script>
          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: 'none' }}
              src={`https://www.facebook.com/tr?id=${tracking.fbPixelId}&ev=PageView&noscript=1`}
              alt=""
            />
          </noscript>
        </>
      )}

      {/* Google Analytics 4 */}
      {tracking.gaId && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${tracking.gaId}`}
            strategy="afterInteractive"
          />
          <Script id="ga4" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${tracking.gaId}');
            `}
          </Script>
        </>
      )}

      {/* Google Tag Manager */}
      {tracking.gtmId && (
        <Script id="gtm" strategy="afterInteractive">
          {`
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${tracking.gtmId}');
          `}
        </Script>
      )}
    </>
  )
}

// --- Event Tracking Helpers ---

declare global {
  interface Window {
    fbq?: (...args: any[]) => void
    gtag?: (...args: any[]) => void
    dataLayer?: any[]
  }
}

export function trackAddToCart(product: {
  id: string
  name: string
  price: number
  variant?: string
  quantity?: number
}) {
  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_ids: [product.id],
      content_name: product.name,
      content_type: 'product',
      value: product.price,
      currency: 'THB',
    })
  }

  // Google Analytics 4
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'add_to_cart', {
      currency: 'THB',
      value: product.price,
      items: [{
        item_id: product.id,
        item_name: product.name,
        item_variant: product.variant,
        price: product.price,
        quantity: product.quantity || 1,
      }],
    })
  }
}

export function trackInitiateCheckout(items: Array<{
  id: string
  name: string
  price: number
  quantity: number
}>, total: number) {
  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'InitiateCheckout', {
      content_ids: items.map(i => i.id),
      num_items: items.reduce((sum, i) => sum + i.quantity, 0),
      value: total,
      currency: 'THB',
    })
  }

  // Google Analytics 4
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'begin_checkout', {
      currency: 'THB',
      value: total,
      items: items.map(i => ({
        item_id: i.id,
        item_name: i.name,
        price: i.price,
        quantity: i.quantity,
      })),
    })
  }
}

export function trackViewContent(product: {
  id: string
  name: string
  price: number
  category?: string
}) {
  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_ids: [product.id],
      content_name: product.name,
      content_type: 'product',
      value: product.price,
      currency: 'THB',
    })
  }

  // Google Analytics 4
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'view_item', {
      currency: 'THB',
      value: product.price,
      items: [{
        item_id: product.id,
        item_name: product.name,
        price: product.price,
      }],
    })
  }
}

export function trackPurchase(order: {
  id: string
  orderNumber: string
  total: number
  items: Array<{ name: string; price: number; quantity: number }>
}) {
  if (typeof window === 'undefined') return

  const contents = order.items.map(i => ({ id: i.name, quantity: i.quantity, item_price: i.price }))

  if (window.fbq) {
    window.fbq('track', 'Purchase', {
      value: order.total,
      currency: 'THB',
      contents,
      content_type: 'product',
      order_id: order.orderNumber,
    })
  }

  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: order.orderNumber,
      value: order.total,
      currency: 'THB',
      items: order.items.map(i => ({
        item_name: i.name,
        price: i.price,
        quantity: i.quantity,
      })),
    })
  }
}
